import { useEffect, useState } from "react";
import { ChevronDown, CheckCircle2, Clock3, MessageSquare } from "lucide-react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getStatusHistory } from "../../store/slices/epcSlice";
import { QuoteStatusLabels } from "../../utils/getQuoteStatusLabel";

interface Props {
  custId: string;
}

export default function StatusHistoryTab({ custId }: Props) {
  const dispatch: RootDispatch = useDispatch();

  useEffect(() => {
    if (custId) dispatch(getStatusHistory(custId));
  }, [dispatch, custId]);

  const quotes = useSelector((state: RootState) => state.epc.statusHistory);
  const [openIndex, setOpenIndex] = useState<number>(0);

  return (
    <div className="max-w-4xl mx-auto p-1 md:p-1">
      <div className="space-y-2">
        {quotes.map((quote, index) => {
          const expanded = openIndex === index;
          const latestStatus =
            quote.statusHistory?.[quote.statusHistory.length - 1];

          const currentStatus = quote.currentStatus;

          return (
            <div
              key={quote._id}
              className="bg-white border rounded-lg shadow-sm"
            >
              {/* HEADER */}
              <button
                className="w-full p-2 flex items-center justify-between hover:bg-gray-50"
                onClick={() => setOpenIndex(expanded ? -1 : index)}
              >
                <div className="text-left space-y-0.5">
                  {/* TOP ROW */}
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">
                      #{quote.quotationNumber}
                    </span>

                    <span className="text-[10px] px-2 py-[2px] rounded bg-blue-50 text-blue-600">
                      {QuoteStatusLabels[
                        currentStatus as keyof typeof QuoteStatusLabels
                      ] || currentStatus}
                    </span>
                  </div>

                  {/* META ROW */}
                  <div className="text-[11px] text-gray-500 flex flex-wrap gap-2">
                    <span>📏 {quote.plantSize}</span>
                    <span>₹{quote.totalCost?.toLocaleString()}</span>
                    <span>{dayjs(quote.createdAt).format("DD MMM YYYY")}</span>
                  </div>

                  {/* INLINE STATUS INFO */}
                  {latestStatus && (
                    <div className="text-[10px] text-gray-400 flex items-center gap-2 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock3 size={10} />
                        {dayjs(latestStatus.updatedAt).format("DD MMM, HH:mm")}
                      </span>

                      <span>•</span>

                      <span>
                        by{" "}
                        <b>
                          {latestStatus.updatedBy?.username ||
                            latestStatus.updatedBy?.name ||
                            "Unknown"}
                        </b>
                      </span>
                    </div>
                  )}
                </div>

                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    expanded ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* TIMELINE */}
              {expanded && (
                <div className="border-t bg-gray-50 p-2">
                  <div className="relative">
                    {quote.statusHistory.map((item, idx) => (
                      <div
                        key={item._id}
                        className="relative flex gap-2 pb-3 last:pb-0 items-center"
                      >
                        <div className="w-5 h-5 rounded-full bg-blue-700 text-white flex items-center justify-center text-[10px]">
                          {idx + 1}
                        </div>

                        <div className="flex-1">
                          <div className="bg-white border rounded-md p-2">
                            <div className="flex justify-between gap-2">
                              <p className="text-xs font-medium">
                                {QuoteStatusLabels[
                                  item.status as keyof typeof QuoteStatusLabels
                                ] || item.status}
                              </p>

                              <span className="text-[10px] text-gray-500 flex items-center gap-1">
                                <Clock3 size={10} />
                                {dayjs(item.updatedAt).format("DD MMM YYYY")}
                              </span>
                            </div>

                            {/* UPDATED BY */}
                            <div className="text-[10px] text-gray-400">
                              by {item.updatedBy?.username}
                            </div>

                            {/* LOAN DETAILS ENRICHMENT */}
                            {quote.loanDetails &&
                              item.status === "LOAN_APPLIED" && (
                                <div className="mt-1 text-[10px] text-gray-600 space-y-[2px]">
                                  <div>
                                    🏦 Bank: <b>{quote.loanDetails.bankName}</b>
                                  </div>

                                  <div>
                                    📅 Applied:{" "}
                                    {dayjs(
                                      quote.loanDetails.dateApplied,
                                    ).format("DD MMM YYYY")}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {quote.loanDetails.isJansmarath && (
                                      <span className="px-1 py-[1px] text-[9px] bg-green-100 text-green-700 rounded">
                                        JanSamarth
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                            {quote.loanDetails &&
                              item.status === "LOAN_APPROVED" && (
                                <div className="mt-1 text-[10px] text-gray-600">
                                  ✅ Approved on{" "}
                                  {dayjs(quote.loanDetails.approvedDate).format(
                                    "DD MMM YYYY",
                                  )}
                                </div>
                              )}

                            {item.status === "LOAN_REJECTED" && (
                              <div className="mt-1 text-[10px] text-red-500">
                                ❌ Rejected on{" "}
                                {dayjs(item.updatedAt).format("DD MMM YYYY")}
                              </div>
                            )}

                            {/* REMARK */}
                            {item.remark && (
                              <div className="mt-1 text-[11px] bg-gray-50 p-1 rounded">
                                <MessageSquare
                                  size={10}
                                  className="inline mr-1"
                                />
                                {item.remark}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
